import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { FieldErrors } from "@/components/form/field-errors";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { createChoreSchedule } from "@repo/api/client";

const choreScheduleFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

type NewChoreScheduleDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function NewChoreScheduleDialog({ open, onOpenChange }: NewChoreScheduleDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: createChoreSchedule,
  });

  const form = useForm({
    defaultValues: { name: "" },
    validators: { onBlur: choreScheduleFormSchema },
    onSubmit: async ({ value }) => {
      const schedule = await mutation.mutateAsync({ name: value.name });
      await queryClient.invalidateQueries({ queryKey: ["chore-schedules"], exact: true });
      onOpenChange(false);
      form.reset();
      navigate(`/chore-schedules/${schedule.id}`);
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) form.reset();
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>New Chore Schedule</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-4"
        >
          <form.Field name="name">
            {(field) => (
              <div className="space-y-1">
                <Label htmlFor="schedule-name">Name</Label>
                <Input
                  id="schedule-name"
                  placeholder="e.g. Weekly chores"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  autoFocus
                />
                <FieldErrors meta={field.state.meta} />
              </div>
            )}
          </form.Field>

          {mutation.isError && (
            <p className="text-sm text-destructive">Failed to create schedule. Please try again.</p>
          )}

          <DialogFooter>
            <form.Subscribe
              selector={(state) => [state.canSubmit, state.isSubmitting, state.isDirty]}
            >
              {([canSubmit, isSubmitting, isDirty]) => (
                <Button type="submit" disabled={!canSubmit || !!isSubmitting || !isDirty}>
                  {isSubmitting ? "Creating…" : "Create"}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
